import { useCallback, useEffect, useMemo, useState } from 'react'

export const WORKSPACE_TABS = [
  'overview',
  'inbox',
  'automations',
  'heal',
  'enterprise',
  'ops'
] as const

export type WorkspaceTab = (typeof WORKSPACE_TABS)[number]

const LABELS: Record<WorkspaceTab, string> = {
  overview: 'Overview',
  inbox: 'Inbox',
  automations: 'Automations',
  heal: 'Heal',
  enterprise: 'Enterprise',
  ops: 'Ops'
}

function parseHash(): WorkspaceTab {
  const raw = (window.location.hash || '').replace(/^#/, '').split('/')[0]
  if (WORKSPACE_TABS.includes(raw as WorkspaceTab)) return raw as WorkspaceTab
  // legacy section ids
  if (raw === 'command') return 'overview'
  if (raw === 'learned' || raw === 'templates' || raw === 'pulse') return 'automations'
  if (raw === 'trust') return 'ops'
  return 'overview'
}

export function useWorkspaceTabs() {
  const [tab, setTabState] = useState<WorkspaceTab>(() =>
    typeof window !== 'undefined' ? parseHash() : 'overview'
  )
  const [visited, setVisited] = useState<Set<WorkspaceTab>>(() => new Set([tab]))

  const setTab = useCallback((next: WorkspaceTab) => {
    setTabState(next)
    setVisited((v) => {
      if (v.has(next)) return v
      const copy = new Set(v)
      copy.add(next)
      return copy
    })
    const url = `#${next}`
    if (window.location.hash !== url) {
      window.history.replaceState(null, '', url)
    }
  }, [])

  useEffect(() => {
    const onHash = () => setTab(parseHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [setTab])

  const tabs = useMemo(
    () =>
      WORKSPACE_TABS.map((id) => ({
        id,
        label: LABELS[id]
      })),
    []
  )

  return { tab, setTab, visited, tabs, labels: LABELS }
}
